import { useState } from "react";
import { Laptop, Wifi, WifiOff, AlertTriangle, ArrowUpCircle, Pencil, Trash2, Check, X, Loader2, Copy } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/useToast";
import {
  useMachines,
  useRenameMachine,
  useRemoveMachine,
  useSetMachineAutoContinue,
} from "@/hooks/queries/use-mailbox";
import { Checkbox } from "@/components/ui/checkbox";
import { machineMinimumVersion, machineReadiness, type Machine, type MachineReadiness } from "@/lib/api/mailbox";
import { MACHINE_UPDATE_COMMAND } from "@mycompbox/shared";
import { ApiError } from "@/lib/api/client";
import { Link } from "react-router-dom";

// =============================================================================
// Readiness badge
// =============================================================================

/**
 * Maps the server-derived {@link MachineReadiness} to a badge (icon + label +
 * variant). Mirrors the readiness copy used by the launcher dialog so the two
 * views stay consistent: 'ready' is the only "good" state; the others are
 * muted/warning. 'outdated' (ENG-322) is layered on by the web from the
 * API-reported minimum CLI version.
 */
function ReadinessBadge({ readiness, title }: { readiness: MachineReadiness; title?: string }) {
  if (readiness === "ready") {
    return (
      <Badge variant="outline" className="gap-1 border-green-500/30 text-green-400">
        <Wifi className="h-3 w-3" />
        Ready
      </Badge>
    );
  }
  if (readiness === "runtime-not-found") {
    return (
      <Badge variant="outline" className="gap-1 border-amber-500/30 text-amber-400">
        <AlertTriangle className="h-3 w-3" />
        Runtime not found
      </Badge>
    );
  }
  if (readiness === "outdated") {
    return (
      <Badge variant="outline" className="gap-1 border-amber-500/30 text-amber-400" title={title}>
        <ArrowUpCircle className="h-3 w-3" />
        Update needed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 border-white/15 text-muted-foreground">
      <WifiOff className="h-3 w-3" />
      Offline
    </Badge>
  );
}

/** Tooltip for the outdated badge: current vs. required version and the fix. */
function outdatedTitle(machine: Machine): string {
  const minimum = machineMinimumVersion(machine);
  const current = machine.cliVersion ? `v${machine.cliVersion}` : "an older version";
  return `Running ${current}${minimum ? `; v${minimum}+ required` : ""}. Run \`${MACHINE_UPDATE_COMMAND}\` on this machine.`;
}

/**
 * Inline `compbox update` command with a copy button, shown on outdated rows
 * (ENG-322). Clipboard failures fall back to a toast telling the user to copy
 * the command by hand.
 */
function UpdateCommandCopy() {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(MACHINE_UPDATE_COMMAND);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      toast(`Couldn't copy — run \`${MACHINE_UPDATE_COMMAND}\` on this machine.`);
    }
  };

  return (
    <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground" data-testid="machine-update-command">
      <span>Run</span>
      <code className="rounded bg-muted px-1 py-0.5">{MACHINE_UPDATE_COMMAND}</code>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        aria-label={copied ? "Copied update command" : "Copy update command"}
        onClick={() => void handleCopy()}
      >
        {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />}
      </Button>
    </div>
  );
}

// =============================================================================
// Machine name length limits (mirror the API's 1–255 trim/length check — UX only)
// =============================================================================

const MACHINE_NAME_MIN = 1;
const MACHINE_NAME_MAX = 255;

/** Returns an inline error for an invalid name, or null when acceptable. */
function validateMachineName(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed.length < MACHINE_NAME_MIN) return "Name can't be empty.";
  if (trimmed.length > MACHINE_NAME_MAX) return `Name must be ${String(MACHINE_NAME_MAX)} characters or fewer.`;
  return null;
}

// =============================================================================
// Single row
// =============================================================================

interface MachineRowProps {
  machine: Machine;
  onRename: (id: string, name: string) => void;
  isRenaming: boolean;
  onRequestRemove: (machine: Machine) => void;
  onToggleAutoContinue: (id: string, autoContinue: boolean) => void;
  isTogglingAutoContinue: boolean;
}

function MachineRow({
  machine,
  onRename,
  isRenaming,
  onRequestRemove,
  onToggleAutoContinue,
  isTogglingAutoContinue,
}: MachineRowProps) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(machine.name);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setDraftName(machine.name);
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError(null);
  };

  const saveEdit = () => {
    const validationError = validateMachineName(draftName);
    if (validationError) {
      setError(validationError);
      return;
    }
    const trimmed = draftName.trim();
    // No-op rename: nothing changed, so just close the editor.
    if (trimmed === machine.name) {
      setEditing(false);
      return;
    }
    onRename(machine.id, trimmed);
    setEditing(false);
  };

  return (
    <div className="flex items-start justify-between gap-3 py-4 first:pt-0 last:pb-0">
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 rounded-md bg-muted p-2">
          <Laptop className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 space-y-1">
          {editing ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Input
                  aria-label="Machine name"
                  value={draftName}
                  autoFocus
                  maxLength={MACHINE_NAME_MAX}
                  onChange={(e) => {
                    setDraftName(e.target.value);
                    if (error) setError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveEdit();
                    if (e.key === "Escape") cancelEdit();
                  }}
                  className="h-8 w-56"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Save name"
                  onClick={saveEdit}
                  disabled={isRenaming}
                >
                  {isRenaming ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 text-green-400" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Cancel rename"
                  onClick={cancelEdit}
                  disabled={isRenaming}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {error && <p className="text-xs text-destructive">{error}</p>}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="truncate font-medium">{machine.name}</span>
              <ReadinessBadge
                readiness={machineReadiness(machine)}
                title={outdatedTitle(machine)}
              />
            </div>
          )}
          {!editing && machine.lastSeenAt && (
            <div className="text-sm text-muted-foreground">
              Last check-in {formatDistanceToNow(new Date(machine.lastSeenAt), { addSuffix: true })}
            </div>
          )}
          {!editing && machine.cliVersion && (
            <div className="text-xs text-muted-foreground" data-testid="machine-cli-version">
              CompBox v{machine.cliVersion}
            </div>
          )}
          {!editing && machineReadiness(machine) === "outdated" && <UpdateCommandCopy />}
          {!editing && !machine.lastSeenAt && (
            <div className="text-sm text-muted-foreground">Never checked in</div>
          )}
          {!editing && (
            <div className="flex items-center gap-2 pt-1 text-sm text-muted-foreground">
              <Checkbox
                id={`auto-continue-${machine.id}`}
                aria-label="Auto-continue stopped builds"
                // Pre-ENG-334 APIs omit the field; the server default is on.
                checked={machine.autoContinue ?? true}
                disabled={isTogglingAutoContinue}
                onCheckedChange={(checked) => {
                  onToggleAutoContinue(machine.id, checked);
                }}
              />
              <span>Start a follow-up run when a build stops partway</span>
            </div>
          )}
        </div>
      </div>

      {!editing && (
        <div className="flex flex-shrink-0 items-center gap-1">
          <Button variant="ghost" size="sm" onClick={startEdit}>
            <Pencil className="h-4 w-4 mr-1" />
            Rename
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onRequestRemove(machine);
            }}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Remove
          </Button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// List
// =============================================================================

/**
 * Machine-management list (ENG-170). Renders the caller's paired (non-revoked)
 * machines with their readiness, supports inline rename, and removes (revokes) a
 * machine behind a confirm dialog. Both mutations invalidate the shared
 * `useMachines` query, so a rename re-renders here and a removal drops the row
 * out of both this list and the "Send to my machine" launcher.
 *
 * The API already excludes revoked machines from GET /machines, so no
 * client-side status filtering is needed.
 */
export function MachineList() {
  const { data: machines, isLoading, isError } = useMachines();
  const renameMachine = useRenameMachine();
  const removeMachine = useRemoveMachine();
  const setAutoContinue = useSetMachineAutoContinue();
  const { toast } = useToast();

  const [machineToRemove, setMachineToRemove] = useState<Machine | null>(null);

  const handleRename = (id: string, name: string) => {
    renameMachine.mutate(
      { id, name },
      {
        onError: (error) => {
          const message =
            error instanceof ApiError ? error.message : "Failed to rename the machine";
          toast(message);
        },
      }
    );
  };

  const handleToggleAutoContinue = (id: string, autoContinue: boolean) => {
    setAutoContinue.mutate(
      { id, autoContinue },
      {
        onError: (error) => {
          const message =
            error instanceof ApiError ? error.message : "Failed to update auto-continue";
          toast(message);
        },
      }
    );
  };

  const handleConfirmRemove = () => {
    if (!machineToRemove) return;
    const id = machineToRemove.id;
    removeMachine.mutate(id, {
      onError: (error) => {
        const message =
          error instanceof ApiError ? error.message : "Failed to remove the machine";
        toast(message);
      },
    });
    setMachineToRemove(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading your machines...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center text-sm text-muted-foreground">
        <AlertTriangle className="h-8 w-8 mb-2 opacity-50 text-amber-400" />
        <p>Couldn&apos;t load your machines.</p>
        <p>Please try again in a moment.</p>
      </div>
    );
  }

  if (!machines || machines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
        <Laptop className="h-8 w-8 mb-2 opacity-50" />
        <p>No paired machines</p>
        <p className="text-sm">
          <Link to="/connect" className="text-primary underline underline-offset-4">
            Connect your computer
          </Link>{" "}
          to run AI work on it.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="divide-y divide-white/[0.06]">
        {machines.map((machine) => (
          <MachineRow
            key={machine.id}
            machine={machine}
            onRename={handleRename}
            isRenaming={renameMachine.isPending}
            onRequestRemove={setMachineToRemove}
            onToggleAutoContinue={handleToggleAutoContinue}
            isTogglingAutoContinue={
              setAutoContinue.isPending && setAutoContinue.variables.id === machine.id
            }
          />
        ))}
      </div>

      {/* Remove (revoke) confirmation — reuses the app's Radix Dialog pattern. */}
      <Dialog
        open={!!machineToRemove}
        onOpenChange={(open) => {
          if (!open) setMachineToRemove(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove machine</DialogTitle>
            <DialogDescription>
              Removing this machine revokes its daemon token and fails any queued or
              in-progress jobs it holds. The machine will need to be paired again (
              <code className="bg-muted px-1 py-0.5 rounded text-xs">compbox configure</code>)
              before it can run agents. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>

          {machineToRemove && (
            <div className="rounded-md border p-4 bg-muted/50">
              <p className="font-medium">{machineToRemove.name}</p>
              {machineToRemove.lastSeenAt && (
                <p className="text-sm text-muted-foreground">
                  Last check-in{" "}
                  {formatDistanceToNow(new Date(machineToRemove.lastSeenAt), {
                    addSuffix: true,
                  })}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setMachineToRemove(null);
              }}
              disabled={removeMachine.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRemove}
              disabled={removeMachine.isPending}
            >
              {removeMachine.isPending ? "Removing..." : "Remove machine"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
